import { useToggle } from '@direct-frontend/stdlib';
import { useCallback } from 'react';

import { SelectWithItemsProps, SingleSelectProps } from '../../../Select';

export interface UseFilterExpressionSelectParams<ValueType extends string, SectionType extends string = ValueType>
    extends Pick<
        Extract<SingleSelectProps<ValueType, SectionType>, SelectWithItemsProps<ValueType, SectionType>>,
        'value' | 'onChange'
    > {
    /**
     * Обработчик, вызываемый при выборе элемента, чтобы сфокусироваться на следующем контроле.
     * В него необходимо передать функцию для фокусирования на следующем контроле.
     * Для FilterExpressionSelect это onOpen, для FilterExpressionInput – это focus
     */
    onFocusNext: () => void;
    /**
     * Необходимо ли сфокусироваться на этом компоненте сразу же при его монтировании
     */
    autoOpen?: boolean;
}

/**
 * @returns {value, isOpen, onOpen, onClose, onChange} – пропсы, которые нужно передать в FilterExpressionSelect
 */
export const useFilterExpressionSelect = function <ValueType extends string, SectionType extends string = ValueType>(
    params: UseFilterExpressionSelectParams<ValueType, SectionType>,
) {
    const { value, onChange: rawOnChange, onFocusNext, autoOpen } = params;

    const { value: isSelectOpen, toggleOn: openSelect, toggleOff: closeSelect } = useToggle(autoOpen);
    const onChange: UseFilterExpressionSelectParams<ValueType, SectionType>['onChange'] = useCallback(
        (newValue: ValueType) => {
            rawOnChange(newValue);
            onFocusNext();
        },
        [rawOnChange, onFocusNext],
    );

    const onClose = useCallback(() => {
        closeSelect();
        onFocusNext();
    }, [onFocusNext, closeSelect]);

    return {
        value,
        isOpen: isSelectOpen,
        onOpen: openSelect,
        onClose,
        onChange,
    };
};
